import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { API_ENDPOINT, ERROR_MESSAGE, LOCAL_STORAGE_KEY, ROUTER_CONFIG } from '@core/configs';
import { Role } from '@core/configs/roles.config';
import { CKPassLoginModel } from '@core/models/interfaces/login.interface';
import { LOG_LOGOUT_LINK_TO_SCREEN_NAME, ScreenName, UserType } from '@core/models/enums';
import { ApiResponse } from '@core/models/interfaces';
import {
  ApiService,
  AutoLogoutService,
  CacheService,
  HttpCancelService,
  keyTriggerSession,
  LocalStorageService,
  RequestService,
} from '@core/services/common';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { SaucerLogService } from '@core/services/apis/saucer-log.service';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  jwtHelperService: JwtHelperService = new JwtHelperService();
  private isLoggedIn = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly http: ApiService,
    private router: Router,
    private autoLogoutService: AutoLogoutService,
    private cancelService: HttpCancelService,
    private requestService: RequestService,
    private localStorageService: LocalStorageService,
    private saucerLogService: SaucerLogService,
    private dialogRef: MatDialog,
    private readonly cacheService: CacheService,
  ) {
    if (this.loggedIn()) {
      this.setIsLoggedIn(true);
    }
  }

  async loginByToken(params: { accessToken: string; refreshToken: string }) {
    const decodeToken = this.jwtHelperService.decodeToken(params.accessToken);

    if (!decodeToken || !decodeToken.userId) {
      throw new Error('Invalid token!');
    }

    const userSessionLocal = this.localStorageService.getUserSession();
    const userSessionObject = {
      accessToken: params.accessToken,
      refreshToken: params.refreshToken,
    };

    if (userSessionLocal) {
      const currentUserId = this.localStorageService.getUserId();
      const isCurrentUser = this.isCurrentUser(params.accessToken, currentUserId);
      if (isCurrentUser) {
        this.localStorageService.setUserSession(userSessionObject);
        this.localStorageService.trigger(keyTriggerSession.setToken);
      } else {
        // Logout and login with the accessToken passed in
        this.logout().then(() => {
          this.localStorageService.setUserSession(userSessionObject);
          setTimeout(() => {
            const fullName = this.localStorageService.getFullName();
            localStorage.setItem(LOCAL_STORAGE_KEY.fullName, fullName);
            this.localStorageService.trigger(keyTriggerSession.setToken);
            const userId = this.localStorageService.getUserId();
            this.localStorageService.setItem(LOCAL_STORAGE_KEY.userId, userId.toString());
            this.localStorageService.setItem(LOCAL_STORAGE_KEY.handleFirstMenu, false);
          }, 500);
        });
      }
    } else {
      this.localStorageService.setUserSession(userSessionObject);
      setTimeout(() => {
        const fullName = this.localStorageService.getFullName();
        localStorage.setItem(LOCAL_STORAGE_KEY.fullName, fullName);
        this.localStorageService.trigger(keyTriggerSession.setToken);
        const userId = this.localStorageService.getUserId();
        this.localStorageService.setItem(LOCAL_STORAGE_KEY.userId, userId.toString());
        this.localStorageService.setItem(LOCAL_STORAGE_KEY.handleFirstMenu, false);
      }, 500);
      // Write login log
      this.saucerLogService.logLogin();
      this.setIsLoggedIn(true);
    }

    this.router.navigate([ROUTER_CONFIG.businessManagement]).then(() =>
      setTimeout(() => {
        this.localStorageService.trigger(keyTriggerSession.skipLogin);
        window.location.reload();
      }, 500),
    );
  }

  get type(): UserType {
    const userSession = this.localStorageService.getUserSession();
    if (userSession && userSession.accessToken) {
      const decode = this.jwtHelperService.decodeToken(userSession.accessToken);
      return decode.type;
    }
    return null;
  }

  get isAccessTokenExpired(): boolean {
    const accessToken = this.localStorageService.getAccessToken();
    if (accessToken) {
      return this.jwtHelperService.isTokenExpired(accessToken);
    }
    return true;
  }

  get isRefreshTokenExpired(): boolean {
    const refreshToken = this.localStorageService.getRefreshToken();
    if (refreshToken) {
      return this.jwtHelperService.isTokenExpired(refreshToken);
    }
    return true;
  }

  setIsLoggedIn(value: boolean) {
    this.isLoggedIn.next(value);
  }

  getIsLoggedIn() {
    return this.isLoggedIn.asObservable();
  }

  loggedIn() {
    if (this.isRefreshTokenExpired) {
      if (this.localStorageService.getItem(LOCAL_STORAGE_KEY.userSession)) {
        this.logout();
      }
      return false;
    } else {
      return true;
    }
  }

  getModuleByToken(token: string): string {
    const decode = this.jwtHelperService.decodeToken(token);
    return decode.module;
  }

  async loginByCKPass(loginData: CKPassLoginModel) {
    return this.http.post(API_ENDPOINT.auth.loginByCareconneConsole, loginData).toPromise();
  }

  requestAccessToken(refreshToken: string): Observable<ApiResponse<any>> {
    if (refreshToken) {
      return this.http.post(API_ENDPOINT.auth.refreshToken, {
        refreshToken,
      });
    } else {
      return throwError(ERROR_MESSAGE.refreshTokenNotFound);
    }
  }

  getRoles(token: string): Role {
    const decoded = this.jwtHelperService.decodeToken(token);
    if (!decoded) {
      return null;
    }
    if (decoded.type === 'CK_PASS' || decoded.userType === 'CK_PASS') {
      return Role.roleCCJAdmin;
    } else if (decoded.userId) {
      return Role.roleCCJAdmin;
    }
  }

  async logout() {
    if (!this.requestService.checkCompletedRequests()) {
      this.cancelService.cancelPendingRequest();
    }
    if (!this.isAccessTokenExpired || !this.isRefreshTokenExpired) {
      // Write logout log
      const pathname: string = location.pathname;
      const keyScreenName =
        LOG_LOGOUT_LINK_TO_SCREEN_NAME.find(v => pathname.indexOf(v.link) >= 0)?.keyScreenName || '';
      const screenName = ScreenName[keyScreenName] || '';
      this.saucerLogService.logLogout(screenName);
      this.callLogout()
        .toPromise()
        .catch(() => {
          this.callLogout();
        });
    }
    this.autoLogoutService.stop();
    this.localStorageService.clear();
    sessionStorage.clear();
    // Clear cache
    this.cacheService.clearAll().catch(e => console.error(e)); // NOSONAR
    this.localStorageService.trigger(LOCAL_STORAGE_KEY.removeToken);
    this.setIsLoggedIn(false);
    await this.router.navigate([ROUTER_CONFIG.auth.login]);
    this.dialogRef.closeAll();
  }

  verifyRefreshToken(): Observable<any> {
    const refreshToken = this.localStorageService.getRefreshToken();
    if (refreshToken) {
      return this.http.post(API_ENDPOINT.auth.verifyRefreshToken, {
        refreshToken,
      });
    } else {
      // tslint:disable-next-line:no-console
      console.error('REFRESH_TOKEN_NOT_FOUND');
      return throwError('REFRESH_TOKEN_NOT_FOUND');
    }
  }

  private callLogout() {
    return this.http.delete(API_ENDPOINT.auth.logout);
  }

  isCurrentUser(token: string, currentUserId: any): boolean {
    const decodeToken = this.jwtHelperService.decodeToken(token);
    if (!decodeToken || !decodeToken.userId) {
      throw new Error('Invalid token!');
    }
    return Number(currentUserId) === Number(decodeToken.userId);
  }
}
